import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { JSON_CONTENT_TYPE } from "constants/app";
import { validateErrorResponse } from "slices/app/actions/error";
import {
  addToJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { setKanbanColumns } from "slices/opportunity/tracked/trackedSlice";
import { mapProposals } from "slices/proposals/actions";
import { fetchProposal } from "slices/proposals/proposal/actions";
import {
  removeProposalById,
  setCategories,
  setGateMode,
} from "slices/proposals/proposalsSlice";

import {
  removeById,
  setIsListInitialized,
  setListData,
} from "./proposalsListSlice";

export const INITIAL_LIST_SORTING = { field: "status", direction: "ASC" };
export const PROPOSALS_LIST_SORT_KEY = "proposals_list_sort";

const HEADERS = {
  headers: {
    "Content-Type": JSON_CONTENT_TYPE,
  },
};

export const fetchProposalsList =
  (sorting = INITIAL_LIST_SORTING) =>
  async (dispatch) => {
    const queryParams = new URLSearchParams({
      sortOrder: sorting.direction,
      sortColumn: sorting.field,
    });
    const url = `/nextgen/proposals/list?${queryParams.toString()}`;

    dispatch(setIsListInitialized(false));
    dispatch(addToJobQueue("proposalsList"));

    try {
      const { data } = await localhost.get(url);
      dispatch(setListData(data || []));
      return data;
    } catch (error) {
      dispatch(validateErrorResponse(error));
      return [];
    } finally {
      dispatch(setIsListInitialized(true));
      dispatch(removeFromJobQueue("proposalsList"));
    }
  };

// Reuses submitProposalMove's PUT/response contract, but also refreshes the
// list rows: a list-triggered move must update both the board sub-slice
// (dashboard.categories/gateMode, read by the dropdown and computeGateInterception)
// and the list rows (so the moved row shows its new stage).
export const moveProposalStageFromList = createAsyncThunk(
  "proposalsList/moveStage",
  async ({ entityId, columnId, position = null, gate = null }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await localhost.put(`/nextgen/proposal-kanban/${entityId}/position`, {
        columnId,
        position,
        gate,
      });
      dispatch(setCategories(mapProposals(data.columns)));
      dispatch(setGateMode(data.gateMode));
      dispatch(fetchProposalsList());
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const archiveProposal =
  (proposalId, { includeOpportunity, archiveStatusId, opportunityArchiveStatusId, archiveNote } = {}) =>
  async (dispatch) => {
    dispatch(addToJobQueue("archiveProposal"));
    try {
      await localhost.post(
        `/nextgen/proposal/${proposalId}/archive`,
        { includeOpportunity, archiveStatusId, opportunityArchiveStatusId, archiveNote },
        HEADERS
      );
      dispatch(removeById(proposalId));
      dispatch(removeProposalById(proposalId));
      if (includeOpportunity) {
        dispatch(setKanbanColumns([]));
      }

      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Proposal archived successfully",
        })
      );
    } catch (err) {
      dispatch(validateErrorResponse(err));
      throw err;
    } finally {
      dispatch(removeFromJobQueue("archiveProposal"));
    }
  };

export const editProposalArchiveReason = ({ id, archiveStatusId, archiveNote }) => async (dispatch) => {
  dispatch(addToJobQueue("editProposalArchiveReason"));
  try {
    await localhost.put(
      `/nextgen/proposal/${id}/archive-reason`,
      { archiveStatusId, archiveNote },
      HEADERS
    );
    dispatch(
      showGlobalAlert({
        variant: alertVariants.success,
        text: "Proposal archive reason updated successfully",
      })
    );
    await dispatch(fetchProposal(id));
  } catch (err) {
    dispatch(validateErrorResponse(err));
    throw err;
  } finally {
    dispatch(removeFromJobQueue("editProposalArchiveReason"));
  }
};

export const fetchArchivedProposals = () => async (dispatch) => {
  dispatch(setIsListInitialized(false));
  dispatch(addToJobQueue("archivedProposals"));
  dispatch(setListData([]));

  try {
    const { data } = await localhost.get("/nextgen/proposal/archived");
    dispatch(setListData(data || []));
  } catch (error) {
    dispatch(validateErrorResponse(error));
  }

  dispatch(setIsListInitialized(true));
  dispatch(removeFromJobQueue("archivedProposals"));
};

export const exportProposalsToCSV = (sorting) => async (dispatch) => {
  dispatch(addToJobQueue("exportProposalsToCSV"));
  try {
    const rsp = await localhost.get("/nextgen/proposal/export/csv", {
      params: {
        sortColumn: sorting?.field,
        sortOrder: sorting?.direction,
      },
    });
    return new Blob([rsp?.data]);
  } catch (err) {
    dispatch(showErrorModal(err));
    return null;
  } finally {
    dispatch(removeFromJobQueue("exportProposalsToCSV"));
  }
};

export const unarchiveProposal =
  (proposalId, includeOpportunity) => async (dispatch) => {
    dispatch(addToJobQueue("unarchiveProposal"));
    try {
      await localhost.post(
        `/nextgen/proposal/${proposalId}/unarchive`,
        { includeOpportunity },
        HEADERS
      );
      dispatch(removeById(proposalId));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Proposal unarchived successfully",
        })
      );
    } catch (err) {
      dispatch(validateErrorResponse(err));
    } finally {
      dispatch(removeFromJobQueue("unarchiveProposal"));
    }
  };
